// ────────────────────────────────────────────────────────────────────────────
// Floating feedback button (lower-left).
//
// Authenticated testers tap to open a small dialog: pick a category, type a
// message, send. Posts to POST /api/feedback. Hidden whenever a sheet/modal is
// open and on the Nex chat / auth routes so it never covers a primary input.
// ────────────────────────────────────────────────────────────────────────────
import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { useAuth } from "./AuthProvider";
import { useSession } from "./SessionProvider";
import { useToast } from "@/hooks/use-toast";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { MessageSquarePlus, Bug, Lightbulb, Heart, MoreHorizontal } from "lucide-react";

type Category = "bug" | "idea" | "love" | "other";

const categories: { value: Category; label: string; icon: typeof Bug }[] = [
  { value: "bug", label: "Bug", icon: Bug },
  { value: "idea", label: "Idea", icon: Lightbulb },
  { value: "love", label: "Love", icon: Heart },
  { value: "other", label: "Other", icon: MoreHorizontal },
];

export function FeedbackButton() {
  const [loc] = useLocation();
  const { isSignedIn, authedFetch } = useAuth();
  const {
    paywallOpen, legalOpen,
    onboardingOpen, nexQuestionnaireOpen, trainerStepOpen,
  } = useSession();
  const { toast } = useToast();

  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState<Category>("idea");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Reset the form whenever the dialog closes so the next opener starts fresh.
  useEffect(() => {
    if (!open) {
      setMessage("");
      setCategory("idea");
      setSubmitting(false);
    }
  }, [open]);

  // External openers (e.g. the open-beta banner's "Send feedback" button)
  // dispatch a window event so they don't need a direct ref into this state.
  useEffect(() => {
    function onOpen() {
      if (isSignedIn) setOpen(true);
    }
    window.addEventListener("nexus-open-feedback", onOpen as EventListener);
    return () => window.removeEventListener("nexus-open-feedback", onOpen as EventListener);
  }, [isSignedIn]);

  // Same hiding rules as NexFab: skip while any sheet/modal is open and on the
  // Nex chat screen. Also hide on auth/portal routes where unauthed users land.
  const anyModalOpen =
    paywallOpen || legalOpen ||
    onboardingOpen || nexQuestionnaireOpen || trainerStepOpen;
  const hideOnRoute =
    loc === "/nex" ||
    loc === "/login" ||
    loc === "/signup" ||
    loc.startsWith("/auth/") ||
    loc.startsWith("/portal/auth/") ||
    // Admin pages: the operator is the person feedback goes TO, and the FAB
    // was sitting on top of admin tables (e.g. the referrals By Trainer list).
    loc.startsWith("/portal/admin") ||
    // PR-E25: the sub-dex (cosmetic-form grid) is a focused MANUAL-edit screen
    // whose own ActionBar strip carries the Manual Edit affordance. The fixed
    // lower-left FAB was overlapping the first sprite cell on this short,
    // few-form grid (IMG_0753: "Raichu" clipped to "chu"), so hide it here so
    // it never occludes grid content. Matches `/dex/:variant/:dexNumber/forms`.
    /^\/dex\/[^/]+\/[^/]+\/forms$/.test(loc);

  if (!isSignedIn || anyModalOpen || hideOnRoute) return null;

  const submit = async () => {
    const trimmed = message.trim();
    if (trimmed.length === 0) {
      toast({ title: "Please add a message.", variant: "destructive" });
      return;
    }
    setSubmitting(true);
    try {
      const res = await authedFetch("/api/feedback", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ category, message: trimmed, route: loc }),
      });
      if (!res.ok) {
        toast({ title: "Could not send feedback.", description: "Please try again.", variant: "destructive" });
        return;
      }
      toast({ title: "Thanks for the feedback.", description: "We'll take a look soon." });
      setOpen(false);
    } catch (err: any) {
      toast({ title: "Could not send feedback.", description: err?.message ?? "Network error", variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <button
        data-testid="button-feedback-fab"
        onClick={() => setOpen(true)}
        aria-label="Send feedback"
        title="Send feedback"
        /**
         * IT DOES NOT FLOAT, AND THAT IS ON PURPOSE. Read this before
         * "fixing" the classes below.
         *
         * The `fixed z-[55]` here does nothing. `hover-elevate` out-specifies
         * both:
         *
         *   .hover-elevate:not(.no-default-hover-elevate) {
         *     position: relative; z-index: 0;
         *   }
         *
         * beats Tailwind's single-class `.fixed`, so the button renders inline
         * at the end of the page instead of pinned to the viewport.
         *
         * That started as an accident. It was made to float on 2026-08-19 and
         * reverted the same day: pinned, it sat on top of every screen and got
         * in the way, and the owner preferred it where it had been. So the
         * classes stay (removing them changes nothing) and this note is the
         * record that inline is the CHOSEN behaviour, not a bug waiting to be
         * found again.
         *
         * The FeatureIdeaPrompt copy points trainers to the bottom of the page
         * to match. If this ever does float again, that copy has to change too.
         */
        className="fixed z-[55] left-3 w-11 h-11 rounded-full flex items-center justify-center shadow-xl hover-elevate"
        style={{
          bottom: "calc(132px + var(--nx-safe-bottom))",
          // Nexus green. It was a cyan/blue gradient — the one floating control
          // on screen wearing a colour the app uses nowhere else, which read as
          // a third-party widget bolted on rather than part of the product.
          background: "linear-gradient(135deg, var(--accent-fill), var(--accent-fill-hover))",
          boxShadow: "0 8px 22px var(--accent-fill-glow-strong), 0 0 0 1px var(--accent-fill-glow)",
        }}
      >
        {/* Dark ink on the bright green, the same pairing the accent CTAs use —
            white on this green does not clear contrast. */}
        <MessageSquarePlus className="w-5 h-5" style={{ color: "var(--text-on-accent)" }} />
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md" data-testid="dialog-feedback">
          <DialogHeader>
            <DialogTitle>Send feedback</DialogTitle>
            <DialogDescription>
              Found a bug, have an idea, or just want to say hi? We read everything.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <p className="text-xs font-medium mb-2 text-muted-foreground">Category</p>
              <div className="grid grid-cols-4 gap-2">
                {categories.map(({ value, label, icon: Icon }) => {
                  const active = category === value;
                  return (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setCategory(value)}
                      data-testid={`button-feedback-cat-${value}`}
                      className="flex flex-col items-center gap-1 rounded-lg p-2.5 text-xs transition-colors hover-elevate"
                      style={{
                        background: active ? "hsl(var(--teal) / 0.15)" : "var(--surface-elev-1)",
                        border: `1px solid ${active ? "hsl(var(--teal))" : "hsl(var(--border))"}`,
                        color: active ? "hsl(var(--teal))" : "hsl(var(--foreground))",
                      }}
                    >
                      <Icon className="w-4 h-4" />
                      <span className="font-medium">{label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <p className="text-xs font-medium mb-2 text-muted-foreground">Message</p>
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="What's on your mind?"
                rows={5}
                maxLength={4000}
                data-testid="input-feedback-message"
                className="resize-none"
              />
              <p className="text-[10px] text-muted-foreground text-right mt-1">
                {message.length} / 4000
              </p>
            </div>

            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setOpen(false)}
                disabled={submitting}
                data-testid="button-feedback-cancel"
              >
                Cancel
              </Button>
              <Button
                onClick={submit}
                disabled={submitting || message.trim().length === 0}
                data-testid="button-feedback-send"
              >
                {submitting ? "Sending..." : "Send"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
